import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import type {
  BulkUpdateIssuesBody,
  BulkUpdateResult,
  CompleteSprintBody,
  CompleteSprintResponse,
  Sprint,
} from '@tasks/contracts';

import { bulkUpdateIssues } from '../../api/issues';
import { completeSprint, createSprint, deleteSprint, startSprint } from '../../api/sprints';
import { useToast } from '../../components/Toast';
import { humanizeError } from '../../lib/errors';
import type { SprintGroupView } from '../../lib/types';

export type BacklogSprintsState = {
  readonly futureSprints: readonly Sprint[];
  readonly completeTarget: SprintGroupView | null;
  readonly setCompleteTarget: (target: SprintGroupView | null) => void;
  readonly sprintToDelete: SprintGroupView | null;
  readonly setSprintToDelete: (target: SprintGroupView | null) => void;
  readonly isCreateSprintOpen: boolean;
  readonly setCreateSprintOpen: (open: boolean) => void;
  readonly newSprintName: string;
  readonly setNewSprintName: (name: string) => void;
  readonly startMutation: ReturnType<typeof useMutation<Sprint, unknown, string>>;
  readonly completeMutation: ReturnType<
    typeof useMutation<CompleteSprintResponse, unknown, { sprintId: string; body: CompleteSprintBody }>
  >;
  readonly createSprintMutation: ReturnType<typeof useMutation<Sprint, unknown, string>>;
  readonly deleteSprintMutation: ReturnType<typeof useMutation<void, unknown, string>>;
  readonly bulkMutation: ReturnType<typeof useMutation<BulkUpdateResult, unknown, BulkUpdateIssuesBody>>;
};

/** Мутации и диалоговое состояние спринтов для экрана бэклога (UC-SPR). */
export function useBacklogSprints(
  projectKey: string,
  sprintGroups: readonly SprintGroupView[],
  invalidate: () => void,
): BacklogSprintsState {
  const toast = useToast();

  const [completeTarget, setCompleteTarget] = useState<SprintGroupView | null>(null);
  const [sprintToDelete, setSprintToDelete] = useState<SprintGroupView | null>(null);
  const [isCreateSprintOpen, setCreateSprintOpen] = useState(false);
  const [newSprintName, setNewSprintName] = useState('');

  const onError = (cause: unknown): void => {
    const human = humanizeError(cause);
    toast.error(human.message, { code: human.code });
  };

  const startMutation = useMutation<Sprint, unknown, string>({
    mutationFn: (sprintId) => startSprint(sprintId),
    onSuccess: invalidate,
    onError,
  });

  const completeMutation = useMutation<CompleteSprintResponse, unknown, { sprintId: string; body: CompleteSprintBody }>({
    mutationFn: (variables) => completeSprint(variables.sprintId, variables.body),
    onSuccess: (result) => {
      setCompleteTarget(null);
      invalidate();
      toast.success(`Спринт завершён: выполнено ${result.completed}, перенесено ${result.moved}`);
    },
    onError,
  });

  const createSprintMutation = useMutation<Sprint, unknown, string>({
    mutationFn: (name) => createSprint(projectKey, { name, goal: null, startDate: null, endDate: null }),
    onSuccess: () => {
      setCreateSprintOpen(false);
      setNewSprintName('');
      invalidate();
    },
    onError,
  });

  const deleteSprintMutation = useMutation<void, unknown, string>({
    mutationFn: (sprintId) => deleteSprint(sprintId),
    onSuccess: () => {
      setSprintToDelete(null);
      invalidate();
    },
    onError,
  });

  const bulkMutation = useMutation<BulkUpdateResult, unknown, BulkUpdateIssuesBody>({
    mutationFn: (body) => bulkUpdateIssues(projectKey, body),
    onSuccess: (result) => {
      if (result.failed.length > 0) {
        toast.error(`Не удалось изменить ${result.failed.length} задач`, { code: 'PARTIAL_FAILURE' });
      }
      invalidate();
    },
    onError,
  });

  const futureSprints = useMemo<readonly Sprint[]>(
    () => sprintGroups.map((group) => toSprint(group)),
    [sprintGroups],
  );

  return {
    futureSprints,
    completeTarget,
    setCompleteTarget,
    sprintToDelete,
    setSprintToDelete,
    isCreateSprintOpen,
    setCreateSprintOpen,
    newSprintName,
    setNewSprintName,
    startMutation,
    completeMutation,
    createSprintMutation,
    deleteSprintMutation,
    bulkMutation,
  };
}

/** Группа бэклога → полное представление спринта для диалогов и селектов. */
export function toSprint(group: SprintGroupView): Sprint {
  return {
    id: group.sprint.id,
    projectId: '',
    name: group.sprint.name,
    goal: group.goal,
    state: group.sprint.state,
    startDate: group.sprint.startDate,
    endDate: group.sprint.endDate,
    startedAt: null,
    completedAt: group.completedAt,
    hasSnapshot: group.sprint.state === 'closed',
    progress: {
      total: group.issues.length,
      done: group.doneCount,
      points: group.totalPoints,
      donePoints: 0,
    },
    createdAt: group.completedAt ?? new Date().toISOString(),
  };
}
